import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import type { BuiltFormDto } from '@core/api/workspace/models/builtFormDto';

export type PublicFormAccessState =
  | 'granted'
  | 'password_required'
  | 'auth_required'
  | 'not_yet_available'
  | 'closed';

export type PublicFormVisibility =
  | 'public'
  | 'workspace'
  | 'password_protected';

export interface ResolvePublicFormData {
  access: PublicFormAccessState;
  name?: string;
  visibility?: PublicFormVisibility;
  definition?: BuiltFormDto;
  formId?: string;
  formVersion?: number;
  requireSubmissionApproval?: boolean;
  maxSubmissions?: number | null;
  formSubmissionDraftRetentionHours?: number;
  availableAt?: string;
  closedAt?: string;
}

export interface ResolvePublicFormApiResponse {
  success: boolean;
  data: ResolvePublicFormData;
}

export interface UnlockPublicFormApiResponse {
  success: boolean;
  data: {
    accessToken: string;
    expiresIn: number;
  };
}

export interface PublicFormAuthOptions {
  respondentAccessToken?: string | null;
  formAccessToken?: string | null;
  draftKey?: string | null;
}

export interface PublicFormDraftPayload {
  submissionId: string;
  formVersion: number;
  answers: Array<{ fieldId: string; value: unknown }>;
  updatedAt: string;
  status: 'draft';
}

export interface GetPublicFormDraftApiResponse {
  success: boolean;
  data: { draft: PublicFormDraftPayload | null };
}

export interface PutPublicFormDraftApiResponse {
  success: boolean;
  data: {
    submissionId: string;
    formVersion: number;
    updatedAt: string;
  };
}

export interface CreatePublicFormSubmissionApiResponse {
  success: boolean;
  data: {
    submissionId: string;
    status: string;
    submittedAt: string;
    reference: string;
  };
}

export interface PublicFieldOption {
  id: string;
  label: string;
  value?: string;
  uniqueKey?: string;
  secondaryLabel?: string;
}

export interface PublicFieldOptionsApiResponse {
  success: boolean;
  data: {
    options: PublicFieldOption[];
    totalCount: number;
  };
}

export type ResolvePublicFormOptions = PublicFormAuthOptions;

@Injectable({ providedIn: 'root' })
export class PublicFormsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.baseUrl.replace(/\/$/, '');

  resolvePublicForm(
    workspaceSlug: string,
    formSlug: string,
    options: ResolvePublicFormOptions = {},
  ): Observable<ResolvePublicFormApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    return this.http.get<ResolvePublicFormApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}`,
      { headers: this.headers(options) },
    );
  }

  unlockPublicForm(
    workspaceSlug: string,
    formSlug: string,
    password: string,
  ): Observable<UnlockPublicFormApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    return this.http.post<UnlockPublicFormApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}/unlock`,
      { password },
    );
  }

  getDraft(
    workspaceSlug: string,
    formSlug: string,
    options: PublicFormAuthOptions = {},
  ): Observable<GetPublicFormDraftApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    return this.http.get<GetPublicFormDraftApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}/submissions/draft`,
      { headers: this.headers(options) },
    );
  }

  putDraft(
    workspaceSlug: string,
    formSlug: string,
    body: {
      answers: Array<{ fieldId: string; value: unknown }>;
      formVersion?: number;
    },
    options: PublicFormAuthOptions = {},
  ): Observable<PutPublicFormDraftApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    return this.http.put<PutPublicFormDraftApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}/submissions/draft`,
      body,
      { headers: this.headers(options) },
    );
  }

  createSubmission(
    workspaceSlug: string,
    formSlug: string,
    body: {
      answers: Array<{ fieldId: string; value: unknown }>;
      submissionId?: string;
    },
    options: PublicFormAuthOptions = {},
  ): Observable<CreatePublicFormSubmissionApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    return this.http.post<CreatePublicFormSubmissionApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}/submissions`,
      body,
      { headers: this.headers(options) },
    );
  }

  listFieldOptions(
    workspaceSlug: string,
    formSlug: string,
    fieldId: string,
    query: { q?: string; limit?: number } = {},
    options: PublicFormAuthOptions = {},
  ): Observable<PublicFieldOptionsApiResponse> {
    const ws = encodeURIComponent(workspaceSlug);
    const form = encodeURIComponent(formSlug);
    const field = encodeURIComponent(fieldId);
    let params = new HttpParams();
    if (query.q?.trim()) {
      params = params.set('q', query.q.trim());
    }
    if (query.limit != null) {
      params = params.set('limit', String(query.limit));
    }
    return this.http.get<PublicFieldOptionsApiResponse>(
      `${this.baseUrl}/api/public/forms/${ws}/${form}/fields/${field}/options`,
      { headers: this.headers(options), params },
    );
  }

  private headers(options: PublicFormAuthOptions): HttpHeaders {
    let headers = new HttpHeaders();
    if (options.respondentAccessToken) {
      headers = headers.set(
        'Authorization',
        `Bearer ${options.respondentAccessToken}`,
      );
    }
    if (options.formAccessToken) {
      headers = headers.set('X-Form-Access-Token', options.formAccessToken);
    }
    if (options.draftKey) {
      headers = headers.set('X-Form-Draft-Key', options.draftKey);
    }
    return headers;
  }
}
